import { Dialog, DialogProps, IconButton, Typography } from "@mui/material"
import { ReactNode } from "react"
import CloseIcon from "reusable/icons/CloseIcon"
import styled from "styled-components"
import colors from "theme/colors"
import { StyledDividerH } from "theme/shared"

const StyledDialog = styled(Dialog)<{
  $backgroundColor: boolean
  $narrow: boolean
  $hideLabels?: boolean
  $hasError?: boolean
  $padding?: string
}>`
  .MuiDialog-paper {
    width: ${(p) => (p?.$hasError ? "560px" : "calc(100% - 40px)")};
    border-radius: 8px;
    background-color: ${(p) => p.theme.colors.white};
    padding: 20px;
    padding-left: ${(p) => (p.$padding ? p.$padding : "20px")};
    padding-right: ${(p) => (p.$padding ? p.$padding : "20px")};
    box-shadow: ${(p) => p.theme.boxShadows.cardShadow};
    max-width: ${(p) => (p.$narrow ? "350px" : "650px")} !important;
  }
  /* mobile size: */
  ${(p) => p.theme.breakpoints.down("sm")} {
    .MuiDialog-paper {
      ${(p) => p?.$hasError && "width: 316px;"}
    }

    .MuiDialog-container {
      height: 100%;
      max-height: 100vh;
      max-width: 560px !important;
      margin: auto;
    }
    .MuiDialog-paperScrollBody {
      margin: auto;
    }
  }
  /* desktop size: */
  ${(p) => p.theme.breakpoints.up("md")} {
    .MuiDialog-paper {
      max-height: none;
      overflow: initial;
    }
  }
  ${(p) => p.theme.breakpoints.down("sm")} {
    .mobile-margin {
      margin: 34px 0;
    }
  }
`

const StyledWrapper = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  form {
    height: 100%;
  }
`

const StyledIconButton = styled(IconButton)`
  position: absolute;
  top: 2px;
  right: 2px;
  z-index: 2;
  ${(p) => p.theme.breakpoints.down("sm")} {
    padding: 0px;
  }
`

const StyledTitle = styled(Typography).attrs({ variant: "h2" })`
  text-align: center;
  font-size: 24px;
  font-weight: 400;
`
const StyledSubtitle = styled(Typography).attrs({ variant: "body1" })`
  text-align: center;
  max-width: 400px;
`

const StyledPopUpTop = styled.div<{
  $hasChildren: boolean
  $hasError?: boolean
}>`
  width: 100%;
  height: 100%;
  display: ${(p) => (p.$hasError ? "none" : "flex")};
  align-items: center;
  flex-direction: column;
  justify-content: center;
  gap: 10px;
  /* if children */
  ${(p) => p.$hasChildren && "margin-bottom: 20px;"}
`

export type ClassicPopUpProps = DialogProps & {
  open: boolean
  setOpen?: (value: boolean) => void
  onClose: () => void
  children?: ReactNode
  icon?: ReactNode
  title?: string
  text?: string
  className?: string
  backgroundColor?: boolean
  withDivider?: boolean
  narrow?: boolean
  dialogClass?: string
  hasError?: boolean
  padding?: string
  arialabelledby?: string
  invertCloseIcon?: boolean
}

const ClassicPopUp = ({
  open,
  setOpen,
  onClose,
  children,
  icon,
  title,
  text,
  hasError,
  className,
  backgroundColor = true,
  withDivider = false,
  narrow = false,
  dialogClass,
  padding,
  arialabelledby,
  invertCloseIcon,
}: ClassicPopUpProps) => {
  const handleClose = () => {
    if (setOpen) {
      setOpen(false)
    }
    if (onClose) {
      onClose()
    }
  }

  return (
    <StyledDialog
      open={open}
      onClose={handleClose}
      scroll="body"
      className={className}
      $backgroundColor={backgroundColor}
      $narrow={narrow}
      classes={{ paper: dialogClass }}
      aria-modal={true}
      tabIndex={-1}
      aria-labelledby={arialabelledby ? arialabelledby : title}
      $hasError={hasError}
      $padding={padding}
    >
      <StyledIconButton onClick={handleClose}>
        <CloseIcon
          width={35}
          height={35}
          aria-label="close window"
          color={invertCloseIcon ? "white" : undefined}
        />
      </StyledIconButton>
      <StyledWrapper>
        <StyledPopUpTop
          $hasError={hasError}
          $hasChildren={children !== undefined || withDivider}
        >
          {icon}
          {title && <StyledTitle>{title}</StyledTitle>}
          {text && <StyledSubtitle>{text}</StyledSubtitle>}
        </StyledPopUpTop>
        {withDivider && (
          <StyledDividerH
            $color={colors.darkGrey}
            $width={0.5}
            $radius={3}
            $mb="20px"
          />
        )}
        {children}
      </StyledWrapper>
    </StyledDialog>
  )
}

export default ClassicPopUp
